import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, RefreshCw, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import StatusBadge, { STATUS_LABELS } from "@/components/StatusBadge";

const STATUS_FILTERS = [
  { key: "", label: "Todos" },
  { key: "pendente", label: "Pendentes" },
  { key: "em_analise", label: "Em análise" },
  { key: "aprovado", label: "Aprovados" },
  { key: "pago", label: "Pagos" },
  { key: "rejeitado", label: "Rejeitados" },
];

export default function AdminReembolsos() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [stats, setStats] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (status) params.status = status;
      if (q.trim()) params.q = q.trim();
      const [{ data }, statsRes] = await Promise.all([
        api.get("/admin/reembolsos", { params }),
        api.get("/admin/stats"),
      ]);
      setItems(data);
      setStats(statsRes.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const onSearchSubmit = (e) => {
    e.preventDefault();
    load();
  };

  const totals = useMemo(() => stats?.reembolsos, [stats]);

  return (
    <div data-testid="admin-reembolsos-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[var(--ldr-text)]">Reembolsos</h1>
          <p className="text-sm text-[var(--ldr-text-muted)] mt-1">
            Todos os pedidos submetidos através de /reembolso.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          className="ldr-btn-outline"
          data-testid="admin-reembolsos-refresh"
        >
          <RefreshCw size={14} /> Atualizar
        </button>
      </div>

      {/* Stats cards */}
      {totals && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6" data-testid="admin-reembolsos-stats">
          <StatCard label="Total" value={totals.total} />
          <StatCard label="Pendentes" value={totals.pendente} tone="pendente" />
          <StatCard label="Em análise" value={totals.em_analise} tone="em_analise" />
          <StatCard label="Aprovados" value={totals.aprovado} tone="aprovado" />
          <StatCard label="Pagos" value={totals.pago} tone="pago" />
          <StatCard label="Rejeitados" value={totals.rejeitado} tone="rejeitado" />
        </div>
      )}

      {/* Filters */}
      <div className="ldr-card p-4 mb-4">
        <div className="flex flex-wrap gap-2 mb-3">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.key || "all"}
              type="button"
              onClick={() => setStatus(f.key)}
              className={[
                "text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors",
                status === f.key
                  ? "bg-[var(--ldr-red)] text-white border-[var(--ldr-red)]"
                  : "bg-white text-[var(--ldr-text)] border-[var(--ldr-border-strong)] hover:border-[var(--ldr-red)]",
              ].join(" ")}
              data-testid={`filter-status-${f.key || "all"}`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <form onSubmit={onSearchSubmit} className="flex gap-2" data-testid="admin-reembolsos-search-form">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ldr-text-muted)]" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Pesquisar por nº processo, nome, email ou nº de compra"
              className="ldr-input pl-9"
              data-testid="admin-reembolsos-search"
            />
          </div>
          <button type="submit" className="ldr-btn-primary">
            Pesquisar
          </button>
        </form>
      </div>

      {/* Table / list */}
      <div className="ldr-card overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-[var(--ldr-text-muted)]" data-testid="admin-reembolsos-loading">
            A carregar…
          </div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center text-[var(--ldr-text-muted)]" data-testid="admin-reembolsos-empty">
            Sem pedidos para os filtros atuais.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-reembolsos-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-[var(--ldr-text-muted)] border-b border-[var(--ldr-border)]">
                  <th className="px-4 py-3">Processo</th>
                  <th className="px-4 py-3">Cliente</th>
                  <th className="px-4 py-3 hidden md:table-cell">Email</th>
                  <th className="px-4 py-3">Estado</th>
                  <th className="px-4 py-3 hidden lg:table-cell">Data</th>
                  <th className="px-4 py-3 w-10" />
                </tr>
              </thead>
              <tbody>
                {items.map((r) => (
                  <tr
                    key={r.id}
                    className="border-b border-[var(--ldr-border)] hover:bg-[var(--ldr-bg-alt)] transition-colors"
                    data-testid={`row-${r.numero_processo}`}
                  >
                    <td className="px-4 py-3 font-semibold text-[var(--ldr-red)]">
                      <Link to={`/admin/reembolsos/${r.numero_processo}`}>{r.numero_processo}</Link>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium">{r.nome}</div>
                      <div className="text-xs text-[var(--ldr-text-muted)] md:hidden">{r.email}</div>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell text-[var(--ldr-text-muted)]">
                      {r.email}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={r.status} />
                    </td>
                    <td className="px-4 py-3 hidden lg:table-cell text-[var(--ldr-text-muted)]">
                      {formatDate(r.created_at)}
                    </td>
                    <td className="px-4 py-3">
                      <Link
                        to={`/admin/reembolsos/${r.numero_processo}`}
                        className="text-[var(--ldr-red)]"
                        data-testid={`open-${r.numero_processo}`}
                      >
                        <ChevronRight size={18} />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, tone }) {
  return (
    <div className="ldr-card p-4">
      <div className="text-xs uppercase tracking-wider text-[var(--ldr-text-muted)]">{label}</div>
      <div className={"text-2xl font-bold mt-1 " + (tone ? "text-[var(--ldr-text)]" : "text-[var(--ldr-text)]")}>
        {value}
      </div>
    </div>
  );
}

function formatDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString("pt-PT", { dateStyle: "short", timeStyle: "short" });
  } catch (_e) {
    return iso;
  }
}

export { STATUS_LABELS };
